// Wire-Format der ausgelieferten Dokumente und die Abbildung auf die internen
// Typen. Getrennt gehalten, damit ein Formatwechsel im Backend genau eine Datei
// im Frontend berührt.
//
// Zwei Transporte, ein Bundle — gespiegelt zu `pipr/publish/wire.py`:
//
//   files_*      Manifest + gehashte JSON-Dateien, `p` verschachtelt,
//                `deltas` in jedem Dokument wiederholt. Ein Verzeichnis je Bank.
//   firestore_*  `pipr/{bank}` + `pipr/{bank}/years/{year}`, `p_flat` flach
//                (Firestore kennt kein Array von Arrays), `bins` und `deltas`
//                nur im Head, das laufende Jahr im Head eingebettet.

import type { Bank, PiprBundle } from "../types";

// ── gemeinsame Teile ─────────────────────────────────────────────────────────

export interface WireMeeting {
    decision_ts: number;
    decided_bp: number | null;
}

interface WireForwardMeeting {
    decision_ts: number;
    p: number[];
    rate_bp: number;
    base_bp: number;
}

interface WirePending {
    pending_bp: number;
    pending_announced_ts: number | null;
    pending_effective_ts: number | null;
}

// ── Datei-Layout ─────────────────────────────────────────────────────────────

export interface WireManifest {
    schema_version: number;
    methodology_version: string;
    cut_time: string;
    cut_tz: string;
    generated_ts: number;
    meetings: string;
    forward: string;
    history: Array<{ year: number; file: string; days: number }>;
}

export interface WireHistory {
    ts: number[];
    n: number;
    deltas: number[];
    anchor_bp: number[];
    pending_bp: number[];
    implied_bp: number[];
    p: number[][];
    meeting_idx: number[];
    flags: number[];
}

export interface WireForward extends WirePending {
    asof_ts: number;
    anchor_bp: number;
    deltas: number[];
    meetings: WireForwardMeeting[];
}

export function toBundle(
    bank: Bank,
    man: WireManifest,
    meetings: WireMeeting[],
    forward: WireForward,
    years: WireHistory[],
): PiprBundle {
    const sorted = [...years].sort((a, b) => a.ts[0] - b.ts[0]);
    const first = sorted[0];

    return {
        schemaVersion: man.schema_version,
        methodologyVersion: man.methodology_version,
        bank,
        cutTime: man.cut_time,
        cutTz: man.cut_tz,
        generatedTs: man.generated_ts,
        meetings: meetings.map(mapMeeting),
        history: {
            ts: sorted.flatMap((y) => y.ts),
            n: sorted.reduce((acc, y) => acc + y.n, 0),
            deltas: first.deltas,
            anchorBp: sorted.flatMap((y) => y.anchor_bp),
            pendingBp: sorted.flatMap((y) => y.pending_bp ?? zeros(y.n)),
            impliedBp: sorted.flatMap((y) => y.implied_bp),
            p: sorted.flatMap((y) => y.p),
            meetingIdx: sorted.flatMap((y) => y.meeting_idx),
            flags: sorted.flatMap((y) => y.flags),
        },
        forward: {
            asofTs: forward.asof_ts,
            anchorBp: forward.anchor_bp,
            pendingBp: forward.pending_bp ?? 0,
            pendingAnnouncedTs: forward.pending_announced_ts ?? null,
            pendingEffectiveTs: forward.pending_effective_ts ?? null,
            deltas: forward.deltas,
            meetings: forward.meetings.map(mapForwardMeeting),
        },
    };
}

// ── Firestore-Layout ─────────────────────────────────────────────────────────

/** Ein Jahresdokument — `pipr/{bank}/years/{year}` oder `head.current_year`. */
export interface WireYear {
    year: number;
    n: number;
    ts: number[];
    anchor_bp: number[];
    pending_bp: number[];
    implied_bp: number[];
    meeting_idx: number[];
    coverage_pct: number[];
    flags: number[];
    /** p_flat[i * bins + j] — Firestore verbietet ein Array von Arrays. */
    p_flat: number[];
}

export interface WireHead {
    schema_version: number;
    methodology_version: string;
    bank: Bank;
    cut_time: string;
    cut_tz: string;
    generated_ts: number;
    bins: number;
    deltas: number[];
    /** alle vorhandenen Jahre, aufsteigend — inklusive des laufenden */
    years: number[];
    meetings: WireMeeting[];
    /** anders als im Datei-Layout ohne eigenes `deltas` — die stehen oben */
    forward: WirePending & {
        asof_ts: number;
        anchor_bp: number;
        meetings: WireForwardMeeting[];
    };
    /** das laufende Jahr, eingebettet, damit ein Seitenaufruf ein Read ist */
    current_year: WireYear;
    /** nur für den Publisher; der Client liest das nicht */
    history_hashes?: Record<string, string>;
}

/** p_flat -> p[day][bin]. */
function fold(flat: number[], bins: number, n: number): number[][] {
    const out: number[][] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = flat.slice(i * bins, (i + 1) * bins);
    return out;
}

function zeros(n: number): number[] {
    return new Array(n).fill(0);
}

/**
 * Head plus die geladenen abgeschlossenen Jahre auf ein Bundle.
 *
 * `closed` darf leer sein — dann zeigt der Chart nur das laufende Jahr, was für
 * einen ersten Render ausreicht und der Vorbereitung des Nachladens entspricht.
 */
export function fromFirestore(head: WireHead, closed: WireYear[]): PiprBundle {
    const years = [...closed, head.current_year]
        .filter((y) => y.n > 0)
        .sort((a, b) => a.ts[0] - b.ts[0]);

    return {
        schemaVersion: head.schema_version,
        methodologyVersion: head.methodology_version,
        bank: head.bank,
        cutTime: head.cut_time,
        cutTz: head.cut_tz,
        generatedTs: head.generated_ts,
        meetings: head.meetings.map(mapMeeting),
        history: {
            ts: years.flatMap((y) => y.ts),
            n: years.reduce((acc, y) => acc + y.n, 0),
            deltas: head.deltas,
            anchorBp: years.flatMap((y) => y.anchor_bp),
            pendingBp: years.flatMap((y) => y.pending_bp ?? zeros(y.n)),
            impliedBp: years.flatMap((y) => y.implied_bp),
            p: years.flatMap((y) => fold(y.p_flat, head.bins, y.n)),
            meetingIdx: years.flatMap((y) => y.meeting_idx),
            flags: years.flatMap((y) => y.flags),
        },
        forward: {
            asofTs: head.forward.asof_ts,
            anchorBp: head.forward.anchor_bp,
            pendingBp: head.forward.pending_bp ?? 0,
            pendingAnnouncedTs: head.forward.pending_announced_ts ?? null,
            pendingEffectiveTs: head.forward.pending_effective_ts ?? null,
            deltas: head.deltas,
            meetings: head.forward.meetings.map(mapForwardMeeting),
        },
    };
}

// ── kleine gemeinsame Mapper ─────────────────────────────────────────────────

function mapMeeting(m: WireMeeting) {
    return { decisionTs: m.decision_ts, decidedBp: m.decided_bp };
}

function mapForwardMeeting(m: WireForwardMeeting) {
    return {
        decisionTs: m.decision_ts,
        p: m.p,
        rateBp: m.rate_bp,
        baseBp: m.base_bp,
    };
}